import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type ArtefactSlidesTemplate, type OfficeTemplateKind } from '../lib/api'
import { officeRefusalText, officeTemplatesQuery, refreshOfficeTemplates } from '../lib/office-templates'
import { TemplateDialog } from './OfficeTemplateList'
import { TemplateMasterSelects } from './TemplateMasterSelects'

/**
 * An artefact's own bid Office template (AB#444), from its row menu in the explorer — where an
 * artefact's settings live: a deck's **Default template** (a PowerPoint template of the bid + the
 * slide master, when it has several) or a prose artefact's **Word template**. "Bid default (<name>)"
 * follows whatever the bid default is; any template of the bid pins the artefact to it.
 *
 * Saving writes the artefact field through `PATCH /v1/artefacts/office-templates` (null = back to
 * the bid default), then refetches what it changes — the outline, the library lists ("used by"),
 * the slides template (open editors redraw). A refusal is worded in place. A viewer sees the
 * current choice as text, and no Save.
 */
export function ArtefactTemplateDialog({
  bid,
  artefactId,
  title,
  type,
  slidesTemplate,
  wordTemplate,
  canEdit,
  onSaved,
  onClose,
}: {
  bid: string
  artefactId: string
  title: string
  type: 'slides' | 'prose'
  slidesTemplate: ArtefactSlidesTemplate | null
  wordTemplate: string | null
  canEdit: boolean
  onSaved: () => void
  onClose: () => void
}) {
  const { t, lang } = useLang()
  const qc = useQueryClient()
  const kind: OfficeTemplateKind = type === 'slides' ? 'slides' : 'word'
  const list = useQuery(officeTemplatesQuery(bid, kind))
  const templates = list.data ?? []
  const bidDefault = templates.find((x) => x.default)
  const current = type === 'slides' ? (slidesTemplate?.templateId ?? '') : (wordTemplate ?? '')

  // '' = the bid default. An id the library no longer holds renders on the bid default too.
  const [picked, setPicked] = useState<string | null>(null)
  const [pickedMaster, setPickedMaster] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const known = (id: string): string => (templates.some((x) => x.id === id) ? id : '')
  const choice = known(picked ?? current)
  const chosen = templates.find((x) => x.id === choice)
  const masters = chosen?.theme?.masters ?? []
  const master =
    pickedMaster ??
    (slidesTemplate && slidesTemplate.templateId === choice ? slidesTemplate.master : (chosen?.theme?.master ?? 0))
  const changed =
    choice !== known(current) ||
    (type === 'slides' && choice !== '' && masters.length > 1 && master !== slidesTemplate?.master)

  const label = t(type === 'slides' ? 'art_tpl_slides_label' : 'tpl_word')
  const defaultLabel = bidDefault
    ? t('art_tpl_bid_default').replace('{name}', () => bidDefault.name)
    : t('art_tpl_bid_default_bare')
  const masterName = (i: number): string => masters[i]?.name || `${t('tpl_slides_master')} ${i + 1}`

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (!canEdit || busy || !changed) return
    setBusy(true)
    setError('')
    try {
      await api.setArtefactOfficeTemplates(
        bid,
        artefactId,
        type === 'slides'
          ? { slidesTemplate: choice ? { templateId: choice, ...(masters.length > 1 && { master }) } : null }
          : { wordTemplate: choice || null },
      )
      await refreshOfficeTemplates(qc, bid)
      onSaved()
    } catch (err) {
      setError(officeRefusalText(err, kind, t, lang))
      setBusy(false)
    }
  }

  return (
    <TemplateDialog
      title={t(type === 'slides' ? 'art_tpl_title_slides' : 'art_tpl_title_word').replace('{name}', () => title)}
      busy={busy}
      onClose={onClose}
    >
      <form className="otpl-form" onSubmit={(e) => void submit(e)}>
        {list.isError ? (
          <p className="brand-hint brand-error" role="alert">
            {t('otpl_load_failed')}
          </p>
        ) : !list.data ? (
          <p aria-live="polite">…</p>
        ) : !canEdit ? (
          <dl className="otpl-readonly">
            <dt>{label}</dt>
            <dd>{chosen ? chosen.name : defaultLabel}</dd>
            {type === 'slides' && chosen && masters.length > 1 && (
              <>
                <dt>{t('tpl_slides_master')}</dt>
                <dd>{masterName(master)}</dd>
              </>
            )}
          </dl>
        ) : (
          <TemplateMasterSelects
            label={label}
            value={choice}
            options={[{ value: '', label: defaultLabel }, ...templates.map((x) => ({ value: x.id, label: x.name }))]}
            masters={type === 'slides' && chosen ? masters : []}
            master={master}
            disabled={busy}
            onTemplate={(id) => {
              setPicked(id)
              setPickedMaster(null)
            }}
            onMaster={setPickedMaster}
          />
        )}
        <p className="brand-hint">{t(type === 'slides' ? 'art_tpl_manage_slides' : 'art_tpl_manage_word')}</p>
        {error && (
          <p className="brand-hint brand-error" role="alert">
            {error}
          </p>
        )}
        <div className="prompt-acts">
          {busy && <span className="brand-spinner" aria-hidden="true" />}
          <button type="button" className="mini ghost" onClick={onClose} disabled={busy}>
            {canEdit ? t('cancel') : t('close')}
          </button>
          {canEdit && (
            <button type="submit" className="mini ok" disabled={busy || !list.data || !changed}>
              {t('brand_save')}
            </button>
          )}
        </div>
      </form>
    </TemplateDialog>
  )
}
